import { api, createApiQuery, fetchApi, SERVER_BASE_URL } from "@/lib/api";
import { type components } from "@malla/shared";

export type MeshNode = components["schemas"]["MeshNodeDTO"];
export type MeshNetwork = components["schemas"]["MeshNetworkDTO"];
export type MeshCredits = components["schemas"]["CreditsResponse"];
export type MeshAudit = components["schemas"]["AuditLogResponse"];
export type MeshPoll = components["schemas"]["PollResponse"];
export type MeshPairingToken = components["schemas"]["PairingTokenResponse"];
export type MeshAcl = components["schemas"]["AclPolicyResponse"];
export type MeshAclRule = components["schemas"]["AclRuleDto"];
export type MeshSelector = components["schemas"]["SelectorDto"];
export type MeshReachability = components["schemas"]["ReachabilityResponse"];
type ErrorModel = components["schemas"]["ErrorModel"];

/** Role/capability a node plays in the mesh. */
export type MeshRole = "client" | "server" | "relay";

/** A node acts as a server/exit of the mesh when its role is `server`. */
export function isServerNode(node: MeshNode): boolean {
  return node.role === "server";
}

const meshKeys = {
  network: (orgId: string) => ["mesh", "network", orgId] as const,
  nodes: (orgId: string) => ["mesh", "nodes", orgId] as const,
  credits: (orgId: string) => ["mesh", "credits", orgId] as const,
  audit: (orgId: string) => ["mesh", "audit", orgId] as const,
  acl: (orgId: string) => ["mesh", "acl", orgId] as const,
  reachability: (orgId: string) => ["mesh", "reachability", orgId] as const,
};

/** Overlay network (CIDR) of the org — 404 until it is provisioned. */
export function createNetworkQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshNetwork | null, ErrorModel>(
    () => meshKeys.network(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/network", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()), retry: false }),
  );
}

/** All mesh nodes of the org (status/liveness included). */
export function createNodesQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshNode[] | null, ErrorModel>(
    () => meshKeys.nodes(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/nodes", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()) }),
  );
}

/** Bandwidth-contribution credits + leaderboard of the org. */
export function createCreditsQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshCredits | null, ErrorModel>(
    () => meshKeys.credits(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/credits", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()) }),
  );
}

/** Tamper-evident connection audit (hash chain) + integrity check of the org. */
export function createAuditQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshAudit | null, ErrorModel>(
    () => meshKeys.audit(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/audit", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()) }),
  );
}

/** The org's ACL policy (ordered reachability rules) + its fingerprint. */
export function createAclQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshAcl | null, ErrorModel>(
    () => meshKeys.acl(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/acl", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()) }),
  );
}

/** The reachability matrix (effect preview) of the org's current policy. */
export function createReachabilityQuery(orgId: () => string | undefined) {
  return createApiQuery<MeshReachability | null, ErrorModel>(
    () => meshKeys.reachability(orgId() ?? "none"),
    async () =>
      api.GET("/api/mesh/reachability", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()) }),
  );
}

/**
 * Replace the org's ACL policy with the given ordered rules. An empty list
 * means "default full mesh" (everyone in the org sees everyone). Rules are
 * evaluated in order (first match wins); default-deny once any rule exists.
 * Authz: org admin — enforced server-side.
 */
export async function setAcl(orgId: string, rules: MeshAclRule[]): Promise<MeshAcl> {
  return fetchApi(api.POST("/api/mesh/acl", { body: { organization_id: orgId, rules } }));
}

/**
 * Long-poll the topology version: resolves when the org's `config_version`
 * advances past `since` (a change that invalidates configs) or when the server
 * times out. This is the robust push (no WebSocket): the panel refetches only
 * when something actually changed. Returns `{ version, changed }`.
 */
export async function pollTopology(
  orgId: string,
  since: number,
  timeoutMs = 25_000,
): Promise<MeshPoll> {
  return fetchApi(
    api.GET("/api/mesh/poll", {
      params: { query: { organizationId: orgId, since, timeoutMs } },
    }),
  );
}

export async function provisionNetwork(orgId: string, cidr: string): Promise<void> {
  await fetchApi(
    api.POST("/api/mesh/provisionNetwork", { body: { organization_id: orgId, cidr } }),
  );
}

export async function enrollNode(
  orgId: string,
  name: string,
  publicKey: string,
  options?: { isExit?: boolean; advertisedRoutes?: string[]; role?: MeshRole },
): Promise<MeshNode> {
  return fetchApi(
    api.POST("/api/mesh/enroll", {
      body: {
        organization_id: orgId,
        name,
        public_key: publicKey,
        is_exit: options?.isExit ?? false,
        advertised_routes: options?.advertisedRoutes ?? [],
        role: options?.role ?? "client",
      },
    }),
  );
}

export async function revokeNode(orgId: string, nodeId: string): Promise<void> {
  await fetchApi(
    api.POST("/api/mesh/revoke", { body: { organization_id: orgId, node_id: nodeId } }),
  );
}

/**
 * Set a node's role/capability. Marking a node `server` makes the control plane
 * offer it as the full-tunnel exit/server for the other nodes of its scope
 * (it starts advertising `0.0.0.0/0`). Authz: the node's owner (personal mesh)
 * or an org admin (group exit) — enforced server-side.
 */
export async function setNodeRole(orgId: string, nodeId: string, role: MeshRole): Promise<void> {
  await fetchApi(
    api.POST("/api/mesh/node/{id}/role", {
      params: { path: { id: nodeId } },
      body: { organization_id: orgId, role },
    }),
  );
}

/**
 * Mint a device pairing token for headless enrollment (the CLI `malla enroll`
 * or a fresh install → server). The plaintext token is returned ONCE; the
 * server stores only its hash. Minting a `server` token requires org admin.
 */
export async function createPairingToken(
  orgId: string,
  role: MeshRole,
  nodeName?: string,
): Promise<MeshPairingToken> {
  return fetchApi(
    api.POST("/api/mesh/pairingToken", {
      body: { organization_id: orgId, role, node_name: nodeName },
    }),
  );
}

export async function sendHeartbeat(
  orgId: string,
  nodeId: string,
  endpoints: string[],
): Promise<void> {
  await fetchApi(
    api.POST("/api/mesh/heartbeat", {
      body: { organization_id: orgId, node_id: nodeId, endpoints },
    }),
  );
}

/**
 * Downloads the wg-quick config the node should run. The server returns the
 * interface + ACL-filtered peers with a `# PrivateKey = <filled in on device>`
 * placeholder — the private key never leaves the device (zero-knowledge).
 */
export async function fetchNodeConfig(orgId: string, nodeId: string): Promise<string> {
  const res = await fetchApi(
    api.GET("/api/mesh/config", {
      params: { query: { organizationId: orgId, nodeId } },
    }),
  );
  return res.config;
}

/**
 * Generates a random 32-byte base64 value to stand in for a device's WireGuard
 * **public** key when enrolling from the panel. The real key material is
 * produced on the device (`wg genkey | wg pubkey`); this placeholder lets the
 * control plane allocate the overlay IP + slot before the device is configured.
 */
export function generateDeviceKeyPlaceholder(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return globalThis.btoa(binary);
}

export { SERVER_BASE_URL };
